import React, {
    useState,
    createContext,
    useContext,
    useRef,
    useEffect,
    useMemo,
} from 'react'

import {
    AppointmentFieldsFragment,
    useConfirmAppointmentMutation,
    useSendAppointmentPromptMutation,
    AppointmentPromptType,
    useMarkAppointmentAsResolvedMutation,
    useRequestChangeForPendingAppointmentMutation,
    useDenyPendingAppointmentMutation,
} from '~graphql/generated/graphql'
import { confirmAppointment, denyAppointment } from '~rest/sync.rest'
import { useGenericToasts } from '~utils/hooks/use-toast-feedback'
import { isCloudPMS } from '~utils/pms-helpers'
import { useClinic } from './clinic-context'

type PendingAppointmentsProviderType = {
    confirmAppointment: (appointmentId: string) => Promise<boolean>
    denyAppointment: (
        appointmentId: string,
        reasonForDenial?: string,
    ) => Promise<boolean>
    requestChange: (
        appointment: AppointmentFieldsFragment,
        isDenial: boolean,
        reasonForDenial: string,
    ) => Promise<boolean>
}

export const PendingAppointmentsContext =
    createContext<PendingAppointmentsProviderType>({
        confirmAppointment: () => Promise.resolve(false),
        denyAppointment: () => Promise.resolve(false),
        requestChange: () => Promise.resolve(false),
    })

export const PendingAppointmentsProvider = ({
    children,
}: {
    children: React.ReactNode
}) => (
    <PendingAppointmentsContext.Provider
        value={{ ...usePendingAppointmentValues() }}
    >
        {children}
    </PendingAppointmentsContext.Provider>
)

function usePendingAppointmentValues(): PendingAppointmentsProviderType {
    const { clinic } = useClinic()
    const { onSuccess, onError } = useGenericToasts()
    const intervalId = useRef<ReturnType<typeof setInterval> | null>(null)

    const [confirmedAppointmentIds, setConfirmedAppointmentIds] = useState<
        string[]
    >([])

    const [denyAppointmentWithMessage] = useDenyPendingAppointmentMutation()
    const [requestChange] = useRequestChangeForPendingAppointmentMutation()

    const [confirmAppointmentMutation] = useConfirmAppointmentMutation()

    useEffect(() => {
        intervalId.current = setInterval(() => {
            setConfirmedAppointmentIds([])
        }, 90000)

        return () => {
            if (intervalId.current) {
                clearInterval(intervalId.current) // TODO: fix this
            }
        }
    }, [])

    const handleRequestChange = async (
        appointment: AppointmentFieldsFragment,
        isDenial: boolean,
        reasonForDenial: string,
    ) => {
        try {
            if (!appointment.clinic?.id) {
                return false
            }

            if (isDenial) {
                const { data: result } = await denyAppointmentWithMessage({
                    variables: {
                        appointmentId: appointment.id,
                        message: reasonForDenial,
                    },
                })

                if (result) {
                    return true
                }
            } else {
                const { data: result } = await requestChange({
                    variables: {
                        appointmentId: appointment.id,
                        message: reasonForDenial,
                    },
                })

                if (result) {
                    return true
                }
            }

            return false
        } catch {
            return false
        }
    }

    const handleConfirmAppointment = async (appointmentId: string) => {
        if (isCloudPMS(clinic?.pms)) {
            try {
                await confirmAppointment(clinic?.id, appointmentId)
                setConfirmedAppointmentIds([
                    ...confirmedAppointmentIds,
                    appointmentId,
                ])
                onSuccess('Appointment confirmed')
                return true
            } catch {
                onError(
                    'Could not find appointment in practice management system to confirm. Please ensure all the information is entered correctly.',
                )
                return false
            }
        } else {
            await confirmAppointmentMutation({
                variables: { id: appointmentId },
                onCompleted: () => {
                    setConfirmedAppointmentIds([
                        ...confirmedAppointmentIds,
                        appointmentId,
                    ])
                    onSuccess('Appointment confirmed')
                },
                onError: e => {
                    onError(e.message)
                },
            })
            return true
        }
    }

    const handleDenyAppointment = async (
        appointmentId: string,
        reasonForDenial?: string,
    ) => {
        try {
            await denyAppointment(clinic?.id, appointmentId, reasonForDenial)
            return true
        } catch {
            return false
        }
    }

    return {
        confirmAppointment: handleConfirmAppointment,
        denyAppointment: handleDenyAppointment,
        requestChange: handleRequestChange,
    }
}

export const usePendingAppointments = () => {
    return useContext(PendingAppointmentsContext)
}
